import React, { useEffect, useMemo, useRef } from "react";
import { Box } from "@mui/material";

/**
 * Renders a Zoho (or other) embed script inside an isolated iframe so scripts
 * execute as provided and are fully cleaned up when the modal unmounts.
 */
const decodeHtmlEntities = (input) => {
  const str = String(input ?? "");
  if (!str) return "";
  if (
    !str.includes("&lt;") &&
    !str.includes("&gt;") &&
    !str.includes("&amp;") &&
    !str.includes("&#")
  ) {
    return str;
  }
  if (typeof document !== "undefined") {
    const textarea = document.createElement("textarea");
    textarea.innerHTML = str;
    return textarea.value;
  }
  return str
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&#x27;/gi, "'")
    .replace(/&#x2F;/gi, "/")
    .replace(/&amp;/g, "&");
};

const ZohoScriptEmbed = ({ html, title = "Zoho Lab Form" }) => {
  const iframeRef = useRef(null);

  const srcDoc = useMemo(() => {
    const body = decodeHtmlEntities(html);
    return `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <style>
      html, body {
        margin: 0 !important;
        padding: 0 !important;
        width: 100% !important;
        min-height: 100% !important;
        background: #fff !important;
        overflow-x: hidden !important;
      }
      /* Force Zoho / embed wrappers to fill the modal width */
      body > *,
      body > div,
      [id^="zf_div"],
      .zf-templateWrapper,
      .zf-tempContDiv,
      .zf-templateWidth,
      .zf-subContWrap,
      .templateWrapper,
      .formWrapper,
      form,
      iframe {
        width: 100% !important;
        max-width: 100% !important;
        box-sizing: border-box !important;
      }
      iframe {
        display: block !important;
        border: 0 !important;
        min-height: 450px;
      }
    </style>
  </head>
  <body>
    ${body}
    <script>
      (function () {
        function stretchEmbeds() {
          var nodes = document.querySelectorAll(
            'iframe, [id^="zf_div"], .zf-templateWrapper, .zf-tempContDiv, .zf-templateWidth, body > div'
          );
          for (var i = 0; i < nodes.length; i++) {
            var el = nodes[i];
            el.style.setProperty("width", "100%", "important");
            el.style.setProperty("max-width", "100%", "important");
            el.removeAttribute("width");
            if (el.tagName === "IFRAME") {
              el.setAttribute("width", "100%");
              if (!el.style.minHeight) {
                el.style.minHeight = "450px";
              }
            }
          }
        }
        stretchEmbeds();
        var observer = new MutationObserver(stretchEmbeds);
        observer.observe(document.body, { childList: true, subtree: true });
        window.addEventListener("load", stretchEmbeds);
        setTimeout(stretchEmbeds, 300);
        setTimeout(stretchEmbeds, 1000);
        setTimeout(stretchEmbeds, 2500);
      })();
    </script>
  </body>
</html>`;
  }, [html]);

  useEffect(() => {
    return () => {
      const iframe = iframeRef.current;
      if (iframe) {
        try {
          iframe.src = "about:blank";
        } catch (e) {
          // ignore
        }
      }
    };
  }, []);

  if (!html) return null;

  return (
    <Box
      component="iframe"
      ref={iframeRef}
      title={title}
      srcDoc={srcDoc}
      sx={{
        width: "100%",
        maxWidth: "100%",
        minHeight: 450,
        height: "100%",
        border: "none",
        display: "block",
        background: "#fff",
      }}
      sandbox="allow-scripts allow-forms allow-same-origin allow-popups allow-popups-to-escape-sandbox"
    />
  );
};

export default ZohoScriptEmbed;
